import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import {
  AUTO_SAVE_STATUS,
  AUDIO_STATUS,
  MAX_AUDIO_DURATION_LIMIT,
  MAX_AUDIO_LIMIT,
  MAX_TOTAL_DURATION_LIMIT,
  MIN_AUDIO_DURATION_LIMIT,
  MAX_FILE_SIZE,
  STEPS,
  ERROR_CODE_VOICE_CLONING_UPLOAD,
} from '@src/constants/professionalVoiceCloneUpload';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { getProfessionalSentences, getTips } from '@src/apis/voiceCloning';
import CommonButton from '@src/new-components/Button';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  MAX_LENGTH_VOICE_NAME,
  MIN_LENGTH_USERNAME,
  VOICE_CLONE_TYPE,
  AUDIO_TYPE,
  VOICE_NAME_REGEX,
} from '@src/constants/voiceCloning';
import { getAudioDuration } from '@src/services/audio';
import moment from 'moment';
import {
  processQualityUpdates as processQualityUpdatesService,
  updateAudiosWithQuality,
  calculateQualityPercent,
} from '@src/services/voiceCloning';
import apis from '@src/apis';
import { uploadAudioLinkToS3 } from '@src/services/upload';
import actions from '@src/redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { ERROR_CODE } from '@src/errors/code';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { VOICE_CLONING_UPLOAD_GUIDE_URL } from '@src/configs';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import UploadAndForm from './UploadAndForm';
import Instructions from './Instructions';
import FooterActions from './FooterActions';
import {
  StyledProfessionalVoiceCloneUpload,
  Wrapper,
  WrapperContent,
} from './index.style';
import TipsList from './TipsList';
import VerifyVoice from './VerifyVoice';
import DenoiseDialog from './DenoiseDialog';

const AUTO_SAVE_DEBOUNCE = 1500;
const POLLING_INTERVAL = 1.3; // 1.3 seconds
const ProfessionalVoiceCloneUpload = ({
  closePopup,
  refetchVoicesList,
  voiceInfo: initialVoiceInfo,
}) => {
  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();

  const improveAudioUploadEnabled = getFeatureValue(
    FEATURE_KEYS.IMPROVE_AUDIO_UPLOAD,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(
    initialVoiceInfo ? STEPS.UPLOAD_AND_FORM : STEPS.INSTRUCTIONS,
  );
  // const [currentStep, setCurrentStep] = useState(STEPS.VERIFY_VOICE);
  const [openTipsList, setOpenTipsList] = useState(false);
  const [professionalSentence, setProfessionalSentence] = useState([]);
  const [audios, setAudios] = useState([]);
  const [tips, setTips] = useState([]);
  const [errorDialog, setErrorDialog] = useState(null);
  const voiceIdRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [adjustingAudioId, setAdjustingAudioId] = useState(null);
  const [isOpenDenoiseDialog, setIsOpenDenoiseDialog] = useState(false);
  const [isLoadingAudios, setIsLoadingAudios] = useState(false);
  const audioRef = useRef(null);
  const pollingCountRef = useRef({});
  const hasFetchedExistingAudiosRef = useRef(false);
  const timerPollingRef = useRef({});
  // Voice info state
  const [voiceInfo, setVoiceInfo] = useState({
    voiceName: '',
    category: '',
    gender: '',
    ageGroup: '',
    region: '',
    province: '',
    description: '',
  });

  // Form data
  const [errors, setErrors] = useState({});

  // Loading states
  const [isLoading, setIsLoading] = useState(false);

  // Auto save states
  const [autoSaveStatus, setAutoSaveStatus] = useState(AUTO_SAVE_STATUS.IDLE);
  const autoSaveTimeoutRef = useRef(null);
  const lastAutoSavePayloadRef = useRef(voiceInfo);

  const progress = useMemo(
    () =>
      Math.min(
        100,
        audios.reduce(
          (acc, audio) => acc + (audio?.qualityImprovement || 0),
          0,
        ),
      ),
    [audios],
  );
  const isProgressBelowThreshold = progress < 30;
  const isProgressBelowSubmissionThreshold = progress < 60;
  const isAutoSaving = autoSaveStatus === AUTO_SAVE_STATUS.SAVING;
  const isUploadingAudios = useMemo(
    () => audios.some((audio) => audio.status === AUDIO_STATUS.UPLOADING),
    [audios],
  );
  const isContinueDisabled =
    (isAutoSaving || isProgressBelowThreshold) &&
    currentStep === STEPS.UPLOAD_AND_FORM;
  const continueTooltip =
    isProgressBelowThreshold && currentStep === STEPS.UPLOAD_AND_FORM
      ? t('voiceCloneProgressRequirement')
      : '';
  const clearAutoSaveTimeout = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (initialVoiceInfo) {
      const initialVoiceInfoData = {
        voiceName: initialVoiceInfo?.name,
        category: initialVoiceInfo?.category?.id,
        gender: initialVoiceInfo?.gender,
        ageGroup: initialVoiceInfo?.ageGroup,
        region: initialVoiceInfo?.locale,
        province: initialVoiceInfo?.province?.id,
        description: initialVoiceInfo?.description,
      };
      setVoiceInfo(initialVoiceInfoData);
      lastAutoSavePayloadRef.current = initialVoiceInfoData;
      voiceIdRef.current = initialVoiceInfo?.id;
    }
  }, [initialVoiceInfo]);

  const handleAutoSave = useCallback(
    async (data) => {
      if (!data?.voiceName) return;
      if (!voiceIdRef?.current) return;

      const voiceNameNormalized = data?.voiceName?.normalize('NFC') ?? '';
      const lastVoiceInfo = lastAutoSavePayloadRef.current;
      const params = {};
      if (
        voiceNameNormalized &&
        voiceNameNormalized !== lastVoiceInfo?.voiceName
      )
        params.name = voiceNameNormalized?.trim();
      if (data?.category) params.categoryId = data?.category;
      if (data?.gender) params.gender = data?.gender;
      if (data?.ageGroup) params.ageGroup = data?.ageGroup;
      if (data?.region) params.locale = data?.region;
      if (data?.province) params.provinceId = data?.province;
      if (data?.description) params.description = data?.description;
      const response = await apis.voiceCloning.updateVoiceCloningVoice(
        voiceIdRef.current,
        params,
      );
      if (!response?.status) {
        setAutoSaveStatus(AUTO_SAVE_STATUS.IDLE);
        if (response.errorCode === ERROR_CODE.VOICE_NAME_EXISTS) {
          setErrors((prev) => ({
            ...prev,
            voiceName: t('voiceNameExistsError'),
          }));
          return;
        }
        if (response.errorCode === ERROR_CODE.VOICE_NAME_INVALID) {
          setErrors((prev) => ({
            ...prev,
            voiceName: t('voiceNameDescription'),
          }));
          return;
        }
      }

      setAutoSaveStatus(AUTO_SAVE_STATUS.SUCCESS);
      lastAutoSavePayloadRef.current = data;
      // Clear errors when API response is successful
      setErrors((prev) => {
        const updated = { ...prev };
        if (updated.voiceName) {
          delete updated.voiceName;
        }
        return updated;
      });
    },
    [currentStep],
  );

  const scheduleAutoSave = useCallback(
    (data) => {
      if (
        currentStep === STEPS.VERIFY_VOICE ||
        currentStep === STEPS.INSTRUCTIONS
      )
        return;
      clearAutoSaveTimeout();
      setAutoSaveStatus(AUTO_SAVE_STATUS.SAVING);
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTimeoutRef.current = null;
        handleAutoSave(data);
      }, AUTO_SAVE_DEBOUNCE);
    },
    [clearAutoSaveTimeout, currentStep, handleAutoSave],
  );

  useEffect(
    () => () => {
      clearAutoSaveTimeout();
    },
    [clearAutoSaveTimeout],
  );

  useEffect(() => {
    const fetchTips = async () => {
      const response = await getTips();
      setTips(response?.result?.tips || []);
    };
    fetchTips();
  }, []);

  const fetchProfessionalSentence = useCallback(async () => {
    const response = await getProfessionalSentences();
    setProfessionalSentence(response?.result?.sentence || []);
  }, []);

  useEffect(() => {
    fetchProfessionalSentence();
  }, [currentStep]);

  const validateVoiceName = useCallback(
    (voiceName) => {
      const newErrors = {};
      const normalizedVoiceName = voiceName?.normalize('NFC') ?? '';
      if (!normalizedVoiceName) {
        newErrors.voiceName = t('requiredField', { field: t('voiceName') });
      } else if (normalizedVoiceName.length < MIN_LENGTH_USERNAME) {
        newErrors.voiceName = t('voiceNameDescription');
      } else if (normalizedVoiceName.length > MAX_LENGTH_VOICE_NAME) {
        newErrors.voiceName = t('voiceNameDescription');
      } else if (!VOICE_NAME_REGEX.test(normalizedVoiceName)) {
        newErrors.voiceName = t('voiceNameDescription');
      }
      return newErrors;
    },
    [voiceInfo, t],
  );

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = { ...errors };
    const voiceNameErrors = validateVoiceName(voiceInfo.voiceName);
    if (Object.keys(voiceNameErrors).length > 0) {
      newErrors.voiceName = voiceNameErrors.voiceName;
    }

    if (!voiceInfo.category) {
      newErrors.category = t('requiredField', { field: t('contentType') });
    }

    if (!voiceInfo.gender) {
      newErrors.gender = t('requiredField', { field: t('gender') });
    }
    if (!voiceInfo.ageGroup) {
      newErrors.ageGroup = t('requiredField', { field: t('ageGroup') });
    }

    if (!voiceInfo.region) {
      newErrors.region = t('requiredField', { field: t('region') });
    }

    if (!voiceInfo.province) {
      newErrors.province = t('requiredField', { field: t('voiceProvince') });
    }
    if (!voiceInfo.description || voiceInfo.description?.trim() === '') {
      newErrors.description = t('requiredField', { field: t('description') });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).filter((key) => newErrors[key]).length === 0;
  }, [voiceInfo, t, errors]);

  // Handle voice info change
  const handleVoiceInfoChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const updated = { ...voiceInfo, [name]: value };
      if (name === 'region') {
        updated.province = '';
      }
      setVoiceInfo(updated);
      const validVoiceName = validateVoiceName(value);
      if (name === 'voiceName' && Object.keys(validVoiceName).length > 0) {
        setErrors((prev) => ({ ...prev, voiceName: validVoiceName.voiceName }));
        return;
      }
      if (updated[name] === voiceInfo[name]) return;
      scheduleAutoSave(updated);

      // Clear error for this field
      setErrors((prev) => ({ ...prev, [name]: false }));
    },
    [voiceInfo, scheduleAutoSave, validateVoiceName],
  );

  // Stop currently playing audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudioId(null);
    setIsPlaying(false);
  }, []);

  // Handle step navigation
  const handleStepChange = useCallback(
    (newStep) => {
      stopAudio();
      setCurrentStep(newStep);
    },
    [stopAudio],
  );

  // Handle create voice
  const handleCreateVoice = useCallback(async () => {
    setIsLoading(true);
    const voiceNameNormalized = voiceInfo?.voiceName?.normalize('NFC') ?? '';

    const params = {
      name: voiceNameNormalized?.trim(),
      type: VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO,
    };

    const response = await apis.voiceCloning.createVoiceCloningVoice(params);
    if (!response?.status) {
      setIsLoading(false);
      if (response.errorCode === ERROR_CODE.VOICE_NAME_EXISTS) {
        setErrors((prev) => ({
          ...prev,
          voiceName: t('voiceNameExistsError'),
        }));
        return;
      }
      if (response.errorCode === ERROR_CODE.VOICE_NAME_INVALID) {
        setErrors((prev) => ({
          ...prev,
          voiceName: t('voiceNameDescription'),
        }));
        return;
      }
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'createVoiceFailed',
        }),
      );
      return;
    }
    lastAutoSavePayloadRef.current = voiceInfo;
    voiceIdRef.current = response?.result?.voice?.id;
    handleStepChange(STEPS.UPLOAD_AND_FORM);
    if (refetchVoicesList) refetchVoicesList();
    setIsLoading(false);
  }, [handleStepChange, validateForm, currentStep, voiceInfo, audios]);

  // Handle close error dialog
  const handleCloseErrorDialog = useCallback(() => {
    setErrorDialog(null);
  }, []);

  const handleConfirm = () => {
    setErrorDialog(null);
    handleStepChange(STEPS.VERIFY_VOICE);
  };

  const renderActionDialog = useCallback(
    ({ onClose, onConfirm, buttonLeftText, buttonRightText }) => (
      <Box
        display="flex"
        justifyContent="flex-end"
        gap="12px"
        p="0 24px 0px 0px"
      >
        <CommonButton
          variant="outlineSmall"
          onClick={onClose}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          customStyle={{
            height: '40px',
            borderRadius: '8px',
            padding: '0 16px',
          }}
        >
          {buttonLeftText || t('close')}
        </CommonButton>
        <CommonButton
          variant="contained"
          onClick={onConfirm}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          customStyle={{
            height: '40px',
            borderRadius: '8px',
            padding: '0 24px',
          }}
        >
          {buttonRightText || t('continue')}
        </CommonButton>
      </Box>
    ),
    [handleCloseErrorDialog, handleConfirm, t],
  );
  // Handle continue button
  const renderUnderstandButton = useCallback(
    () => (
      <CommonButton
        variant="contained"
        onClick={handleCloseErrorDialog}
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
          height: '40px',
          textTransform: 'none',
          marginRight: '12px',
        }}
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
      >
        {t('gotIt')}
      </CommonButton>
    ),
    [handleCloseErrorDialog, t],
  );

  const openLowProgressDialog = useCallback(() => {
    setErrorDialog({
      name: 'low-progress-warning',
      title: t('voiceCloneProgressWarningTitle'),
      description: t('voiceCloneProgressWarningDescription'),
      open: true,
      showIcon: false,
      onClose: handleCloseErrorDialog,
      actionComponent: renderActionDialog({
        onClose: handleCloseErrorDialog,
        onConfirm: handleConfirm,
        buttonLeftText: t('close'),
        buttonRightText: t('continue'),
      }),
      variant: null,
      maxWidth: '512px',
      padding: '12px 24px',
      paddingHeader: '8px 24px',
      paddingAction: '0px 24px 24px 24px',
      widthContent: '512px',
    });
  }, [handleCloseErrorDialog, renderActionDialog, handleConfirm, t]);

  const openSimpleWarningDialog = useCallback(
    ({ name, description }) => {
      setErrorDialog({
        name,
        title: t('noteV2'),
        description,
        open: true,
        showIcon: false,
        onClose: handleCloseErrorDialog,
        actionComponent: renderUnderstandButton(),
        variant: null,
        maxWidth: '512px',
        padding: '12px 24px',
        paddingHeader: '8px 24px',
        paddingAction: '0px 24px 24px 24px',
        widthContent: '512px',
      });
    },
    [handleCloseErrorDialog, renderUnderstandButton, t],
  );

  const handleContinue = useCallback(() => {
    if (currentStep === STEPS.INSTRUCTIONS) {
      const voiceNameErrors = validateVoiceName(voiceInfo.voiceName);
      if (Object.keys(voiceNameErrors).length > 0) {
        setErrors((prev) => ({
          ...prev,
          voiceName: voiceNameErrors.voiceName,
        }));
        return;
      }
      handleCreateVoice();
    } else if (currentStep === STEPS.UPLOAD_AND_FORM) {
      if (!validateForm()) {
        return;
      }
      const hasInvalidAudios = audios.some((audio) => {
        if (!audio) return false;
        const isFileSizeValid =
          typeof audio.size === 'number' && audio.size <= MAX_FILE_SIZE;
        const isDurationValid =
          typeof audio.duration === 'number' &&
          audio.duration >= MIN_AUDIO_DURATION_LIMIT &&
          audio.duration <= MAX_AUDIO_DURATION_LIMIT;
        const isValidationFailed = !isFileSizeValid || !isDurationValid;
        const isErrorStatus = audio.status === AUDIO_STATUS.ERROR;
        const isPendingStatus =
          audio.status === AUDIO_STATUS.UPLOADING ||
          audio.status === AUDIO_STATUS.CHECKING_QUALITY;
        return isValidationFailed || isErrorStatus || isPendingStatus;
      });

      if (hasInvalidAudios) {
        openSimpleWarningDialog({
          name: 'invalid-or-pending-audios-warning',
          description: t('voiceClonePendingOrInvalidAudiosWarning'),
        });
        return;
      }
      if (isProgressBelowSubmissionThreshold) {
        openLowProgressDialog();
        return;
      }
      handleConfirm();
    }
  }, [
    audios,
    currentStep,
    handleStepChange,
    validateForm,
    handleCreateVoice,
    isProgressBelowSubmissionThreshold,
    openLowProgressDialog,
    openSimpleWarningDialog,
  ]);

  // Handle close
  const onCloseDialog = useCallback(() => {
    setErrorDialog(null);
    closePopup();
    if (refetchVoicesList && currentStep !== STEPS.INSTRUCTIONS)
      refetchVoicesList();
  }, [closePopup, refetchVoicesList, currentStep]);

  const handleClose = useCallback(() => {
    if (isUploadingAudios) {
      setErrorDialog({
        name: 'close-while-uploading',
        title: t('noteV2'),
        description: t('closeWhileUploadingDescription'),
        open: true,
        showIcon: false,
        onClose: () => setErrorDialog(null),
        actionComponent: renderActionDialog({
          onClose: onCloseDialog,
          onConfirm: () => setErrorDialog(null),
          buttonLeftText: t('cancelSynthesisProject'),
          buttonRightText: t('keepOnSynthesisProject'),
        }),
        variant: null,
        maxWidth: '512px',
        padding: '12px 24px',
        paddingHeader: '8px 24px',
        paddingAction: '0px 24px 24px 24px',
        widthContent: '512px',
      });
      return;
    }
    onCloseDialog();
  }, [
    closePopup,
    refetchVoicesList,
    currentStep,
    isUploadingAudios,
    t,
    onCloseDialog,
  ]);

  // Handle tips click
  const handleOpenTipsList = useCallback(() => {
    setOpenTipsList(true);
  }, []);

  // Handle close tips list
  const handleCloseTipsList = useCallback(() => {
    setOpenTipsList(false);
  }, []);

  // Handle file upload
  const uploadAudioAndGetUrl = useCallback(async (audio, directory) => {
    const rawName = audio.name || '';
    const dotIndex = rawName.lastIndexOf('.');
    const fileName =
      dotIndex > 0
        ? rawName.slice(0, dotIndex)
        : rawName || audio.id || `audio-${Date.now()}`;
    const extension =
      dotIndex > -1 && dotIndex < rawName.length - 1
        ? rawName.slice(dotIndex + 1).toLowerCase()
        : AUDIO_TYPE.WAV;
    const uploadResponse = await uploadAudioLinkToS3(
      fileName,
      directory,
      extension,
      audio,
    );
    if (!uploadResponse || uploadResponse.error || !uploadResponse.url) {
      return null;
    }
    return uploadResponse.url;
  }, []);
  const updateAudioById = (audioId, payload) => {
    setAudios((prev) =>
      prev.map((audio) =>
        audio.id === audioId ? { ...audio, ...payload } : audio,
      ),
    );
  };

  // Helper function to mark all checking audios as error
  const markCheckingAudiosAsError = useCallback((errorMessage, audioId) => {
    setAudios((prev) =>
      prev.map((audio) =>
        audio.id === audioId
          ? { ...audio, errorMessage, status: AUDIO_STATUS.ERROR }
          : audio,
      ),
    );
  }, []);

  // Process quality updates from API response
  const processQualityUpdates = useCallback(
    (audiosWithQuality, checkingIds) => {
      // Process quality updates
      const audioUpdates = processQualityUpdatesService(
        audiosWithQuality,
        checkingIds,
      );

      // Update audios in a single setState call if there are updates
      if (Object.keys(audioUpdates).length > 0) {
        setAudios((prev) =>
          updateAudiosWithQuality({ audios: prev, audioUpdates }),
        );
      }
    },
    [],
  );

  const handlePollingError = useCallback(
    (audioId) => {
      markCheckingAudiosAsError(t('checkAudioQualityFailed'), audioId);
    },
    [markCheckingAudiosAsError, t],
  );
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // Start polling to check audio quality for all audios in CHECKING_QUALITY status
  const startPollingAudioQuality = useCallback(
    async (audioId, audioDuration) => {
      try {
        // Validate prerequisites
        if (!audioId) {
          return;
        }
        const count = pollingCountRef.current[audioId] || 0;
        pollingCountRef.current[audioId] = count + 1;
        const timer =
          (POLLING_INTERVAL ** pollingCountRef.current[audioId] / 60) * 100000;

        await sleep(timer);
        // Call API to get quality results
        if (timerPollingRef.current[audioId] > audioDuration * 1000) {
          timerPollingRef.current[audioId] = 0;
          handlePollingError(audioId);
          return;
        }

        const audiosResponse = await apis.voiceCloning.getVoiceCloningAudio(
          audioId,
        );
        if (audiosResponse?.status && audiosResponse?.result?.audio) {
          processQualityUpdates([audiosResponse.result.audio], [audioId]);
          if (audiosResponse?.result?.audio?.quality?.acceptedPercent) {
            timerPollingRef.current[audioId] = 0;
          } else if (audiosResponse?.result?.audio?.status === 'failed') {
            timerPollingRef.current[audioId] = 0;
            setAudios((prev) =>
              prev.map((audio) =>
                audio.id === audioId
                  ? {
                      ...audio,
                      status: AUDIO_STATUS.SUCCESS,
                      qualityImprovement: 0,
                    }
                  : audio,
              ),
            );
          } else {
            startPollingAudioQuality(audioId, audioDuration);
            timerPollingRef.current[audioId] =
              (timerPollingRef.current[audioId] || 0) + timer;
          }
        } else {
          handlePollingError(audioId);
          timerPollingRef.current[audioId] = POLLING_INTERVAL;
        }
      } catch (error) {
        // Handle network or unexpected errors
      }
    },
    [processQualityUpdates, handlePollingError],
  );

  const fetchExistingAudios = useCallback(async () => {
    if (hasFetchedExistingAudiosRef.current) return;
    if (!voiceIdRef.current) return;
    try {
      setIsLoadingAudios(true);
      const response = await apis.voiceCloning.getVoiceCloningAudios(
        voiceIdRef.current,
      );
      setIsLoadingAudios(false);
      if (!response?.status) {
        hasFetchedExistingAudiosRef.current = true;
        return;
      }

      const fetchedAudios = response?.result?.audios || [];
      const mappedAudios = fetchedAudios.map((audio) => {
        const hasFailed = audio?.status === 'failed';
        const hasQuality =
          audio?.status === 'passed' || audio?.status === 'success';

        let qualityImprovement;
        if (hasFailed) {
          qualityImprovement = 0;
        } else if (hasQuality) {
          qualityImprovement = calculateQualityPercent(
            audio.quality,
            audio.denoise,
          );
        }
        return {
          id: audio.id,
          name: audio.name,
          size: audio.size,
          duration: audio.duration,
          audioLink: audio.audioLink,
          denoise: audio.denoise,
          status:
            hasQuality || hasFailed
              ? AUDIO_STATUS.SUCCESS
              : AUDIO_STATUS.CHECKING_QUALITY,
          progress: hasQuality ? 100 : 0,
          quality: audio.quality,
          qualityImprovement,
          errorMessage: null,
        };
      });

      setAudios(mappedAudios);

      if (mappedAudios.length > 0) {
        mappedAudios
          .filter((audio) => audio?.status !== AUDIO_STATUS.SUCCESS)
          .forEach((audio) => {
            startPollingAudioQuality(audio.id, audio.duration);
          });
      }
    } finally {
      hasFetchedExistingAudiosRef.current = true;
    }
  }, [startPollingAudioQuality]);

  useEffect(() => {
    fetchExistingAudios();
  }, [fetchExistingAudios, initialVoiceInfo, currentStep]);

  const showPopupUploadAudioFailed = useCallback(
    (isMaxLimit) => {
      if (isMaxLimit) {
        openSimpleWarningDialog({
          name: 'max-audio-limit-error',
          description: t('maxAudioLimitExceeded', {
            limit: MAX_AUDIO_LIMIT,
          }),
        });
        return;
      }
      openSimpleWarningDialog({
        name: 'max-duration-limit-error',
        description: t('maxTotalDurationExceeded'),
      });
    },
    [openSimpleWarningDialog, t],
  );

  const handleFileUpload = useCallback(
    async (files) => {
      if (audios.length + files.length > MAX_AUDIO_LIMIT) {
        showPopupUploadAudioFailed(true);
        return;
      }

      const filesWithDuration = await Promise.all(
        files.map(async (file) => {
          const audioId = `audio-${uuidv4()}`;
          let duration = 0;
          try {
            const computedDuration = await getAudioDuration(file);
            duration = Number.isFinite(computedDuration) ? computedDuration : 0;
          } catch (error) {
            duration = 0;
          }

          return {
            id: file.id || audioId,
            name: file.name,
            size: file.size,
            duration,
            type: file.type,
            file,
          };
        }),
      );

      const totalAudioSelectedDuration = filesWithDuration.reduce(
        (acc, audio) => acc + audio.duration,
        0,
      );
      const totalAudioDuration = audios.reduce(
        (acc, audio) => acc + audio.duration,
        0,
      );
      const totalDuration = totalAudioSelectedDuration + totalAudioDuration;
      if (totalDuration > MAX_TOTAL_DURATION_LIMIT) {
        showPopupUploadAudioFailed(false);
        return;
      }

      const uploadingAudios = filesWithDuration.map((audio) => {
        const isFileSizeValid = audio.size <= MAX_FILE_SIZE;
        const isDurationValid =
          audio.duration >= MIN_AUDIO_DURATION_LIMIT &&
          audio.duration <= MAX_AUDIO_DURATION_LIMIT;
        const isValid = isFileSizeValid && isDurationValid;

        return {
          ...audio,
          status: isValid ? AUDIO_STATUS.UPLOADING : null,
          progress: isValid ? 0 : undefined,
          errorMessage: null,
        };
      });

      setAudios((prev) => [...prev, ...uploadingAudios]);

      const directory = `voice-cloning/temp-voices/${voiceIdRef.current}`;

      const audioToUpload = uploadingAudios.filter(
        (audio) =>
          audio.size <= MAX_FILE_SIZE &&
          audio.duration >= MIN_AUDIO_DURATION_LIMIT &&
          audio.duration <= MAX_AUDIO_DURATION_LIMIT,
      );

      audioToUpload.map(async (audio) => {
        try {
          const audioUrl = await uploadAudioAndGetUrl(audio.file, directory);
          if (!audioUrl) {
            updateAudioById(audio.id, {
              status: AUDIO_STATUS.ERROR,
              errorMessage: t('uploadAudioFailed'),
            });
            return;
          }

          // Call API to create audio training
          const createAudioResponse =
            await apis.voiceCloning.createVoiceCloningAudio({
              audioLink: audioUrl,
              name: audio.name,
              duration: audio.duration,
              size: audio.size,
              type: VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO,
              voiceId: voiceIdRef.current,
              denoise: audio.denoise,
            });

          if (
            !createAudioResponse?.status ||
            !createAudioResponse?.result?.audio
          ) {
            if (
              createAudioResponse?.errorCode ===
              ERROR_CODE_VOICE_CLONING_UPLOAD.MAX_TOTAL_DURATION_REACHED
            ) {
              showPopupUploadAudioFailed(false);
              setAudios((prev) => prev.filter((a) => a.id !== audio.id));
              return;
            }
            updateAudioById(audio.id, {
              status: AUDIO_STATUS.ERROR,
              errorMessage: t('uploadAudioFailed'),
            });
            return;
          }

          // Update audio with response data
          const createdAudio = createAudioResponse.result.audio;
          const audioIdFromResponse = createdAudio.id;

          // Update audio with created audio data
          updateAudioById(audio.id, {
            id: audioIdFromResponse,
            status: AUDIO_STATUS.CHECKING_QUALITY,
            progress: 100,
            audioLink: audioUrl,
          });

          // Call API to check audio quality (triggers quality check process)
          const checkQualityResponse =
            await apis.voiceCloning.checkAudioQuality(audioIdFromResponse, {
              denoise: audio.denoise || false,
            });

          if (checkQualityResponse?.status) {
            // Start polling for quality results (will only start if not already running)
            startPollingAudioQuality(audioIdFromResponse);
          } else {
            // If quality check fails, mark as error
            updateAudioById(audioIdFromResponse, {
              status: AUDIO_STATUS.ERROR,
              errorMessage: t('checkAudioQualityFailed'),
            });
          }
        } catch (error) {
          updateAudioById(audio.id, {
            status: AUDIO_STATUS.ERROR,
            errorMessage: t('uploadAudioFailed'),
          });
        }
      });
    },
    [
      audios,
      dispatch,
      handleCloseErrorDialog,
      renderUnderstandButton,
      t,
      uploadAudioAndGetUrl,
      startPollingAudioQuality,
    ],
  );

  const deleteAudio = useCallback(
    async (id) => {
      setAudios((prev) => prev.filter((audio) => audio.id !== id));
      // Stop audio if the deleted audio was playing
      if (playingAudioId === id) {
        stopAudio();
      }
    },
    [stopAudio, playingAudioId],
  );
  const handleDeleteAudio = useCallback(
    async (id) => {
      const targetAudio = audios.find((audio) => audio.id === id);
      if (!targetAudio) return;
      if (!targetAudio.audioLink) {
        deleteAudio(id);
        return;
      }

      updateAudioById(id, { deleting: true });

      const response = await apis.voiceCloning.deleteUploadedAudio(id);
      if (!response?.status) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('deleteAudioFailed'),
          }),
        );
        updateAudioById(id, { deleting: false });
        return;
      }

      deleteAudio(id);

      // Polling will automatically stop if no audios are checking quality
    },
    [audios, handleCloseErrorDialog, playingAudioId, renderUnderstandButton, t],
  );

  const handlePlayAudio = useCallback(
    (audio) => {
      if (!audio?.audioLink && !audio?.file) return;

      // If clicking on the same audio that's currently playing, toggle pause
      if (playingAudioId === audio.id && audioRef.current) {
        if (!audioRef.current.paused) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
        return;
      }

      // Stop currently playing audio if any
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (audio.file) {
        audioRef.current = new Audio(URL.createObjectURL(audio.file));
      } else {
        audioRef.current = new Audio(audio.audioLink);
      }
      setPlayingAudioId(audio.id);
      setIsPlaying(true);
      // Create new audio element and play
      audioRef.current.addEventListener('ended', () => {
        setPlayingAudioId(null);
        audioRef.current = null;
      });

      audioRef.current.addEventListener('error', () => {
        setPlayingAudioId(null);
        audioRef.current = null;
      });

      audioRef.current.play().catch(() => {
        setPlayingAudioId(null);
        audioRef.current = null;
      });
    },
    [playingAudioId],
  );

  const handleAdjustAudio = useCallback((audio) => {
    setAdjustingAudioId(audio.id);
    setIsOpenDenoiseDialog(true);
  }, []);

  const adjustingAudio = useMemo(
    () => audios.find((audio) => audio.id === adjustingAudioId),
    [audios, adjustingAudioId],
  );

  const handleCloseDenoiseDialog = useCallback(() => {
    setIsOpenDenoiseDialog(false);
    setAdjustingAudioId(null);
  }, []);

  const handleSaveDenoiseSetting = useCallback(
    async ({ audioId: targetAudioId, denoise }) => {
      if (!targetAudioId) return;
      updateAudioById(targetAudioId, {
        denoise,
        status: AUDIO_STATUS.CHECKING_QUALITY,
        progress: 0,
      });
      handleCloseDenoiseDialog();

      try {
        const response = await apis.voiceCloning.checkAudioQuality(
          targetAudioId,
          { denoise },
        );

        const checkedAudio = response?.result?.audio;
        const isCheckQualitySuccess =
          response?.status && checkedAudio?.quality?.acceptedPercent;
        if (
          isCheckQualitySuccess &&
          (checkedAudio.quality.acceptedPercent.original ||
            checkedAudio.quality.acceptedPercent.withDenoise)
        ) {
          updateAudioById(targetAudioId, {
            status: AUDIO_STATUS.SUCCESS,
            quality: checkedAudio.quality,
            qualityImprovement: calculateQualityPercent(
              checkedAudio.quality,
              checkedAudio.denoise,
            ),
          });
          return;
        }

        startPollingAudioQuality(targetAudioId);
      } catch (error) {
        startPollingAudioQuality(targetAudioId);
      }
    },
    [handleCloseDenoiseDialog, startPollingAudioQuality],
  );

  // Cleanup audio and polling intervals on unmount
  useEffect(
    () => () => {
      stopAudio();
    },
    [stopAudio],
  );

  const maxAttempts = useMemo(() => {
    const attempts = initialVoiceInfo?.verificationAttempts;
    const lastTime = attempts?.lastTime ? moment(attempts.lastTime) : null;

    if (attempts?.remaining === 0 && lastTime) {
      const unlockTime = lastTime.clone().add(24, 'hours');
      return moment().isBefore(unlockTime);
    }

    return false;
  }, [initialVoiceInfo]);

  useEffect(() => {
    if (maxAttempts && improveAudioUploadEnabled) {
      handleStepChange(STEPS.VERIFY_VOICE);
    }
  }, [maxAttempts, improveAudioUploadEnabled]);

  // Render step component
  const renderStep = () => {
    switch (currentStep) {
      case STEPS.INSTRUCTIONS:
        return (
          <Instructions
            voiceName={voiceInfo.voiceName}
            onVoiceNameChange={handleVoiceInfoChange}
            error={errors}
          />
        );
      case STEPS.UPLOAD_AND_FORM:
        return (
          <UploadAndForm
            voiceInfo={voiceInfo}
            errors={errors}
            onVoiceInfoChange={handleVoiceInfoChange}
            disabled={isLoading}
            onOpenTips={handleOpenTipsList}
            onFileUpload={handleFileUpload}
            progress={progress}
            audios={audios}
            onDelete={handleDeleteAudio}
            onPlay={handlePlayAudio}
            playingAudioId={playingAudioId}
            isPlaying={isPlaying}
            onAdjust={handleAdjustAudio}
            isLoadingAudios={isLoadingAudios}
          />
        );
      case STEPS.VERIFY_VOICE:
        return (
          <VerifyVoice
            refetchProfessionalSentence={fetchProfessionalSentence}
            professionalSentence={professionalSentence}
            handleClose={handleClose}
            voiceId={voiceIdRef.current}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledProfessionalVoiceCloneUpload>
      <Wrapper>
        <WrapperContent>
          {currentStep === STEPS.VERIFY_VOICE && (
            <CommonButton
              startIcon={
                <ArrowBackRoundedIcon
                  sx={{ fontSize: '24px !important', color: COLOR.darkBlue }}
                />
              }
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.darkBlue,
              }}
              onClick={() => {
                handleStepChange(STEPS.UPLOAD_AND_FORM);
              }}
              customStyle={{
                height: '40px',
                position: 'absolute',
                top: '40px',
                left: '40px',
              }}
            >
              {t('back')}
            </CommonButton>
          )}
          <CommonButton
            onClick={handleClose}
            aria-label="close"
            variant="outlineSmall"
            customStyle={{
              width: '40px',
              height: '40px',
              minWidth: '40px',
              alignSelf: 'flex-end',
              position: 'absolute',
              top: '40px',
              right: '40px',
            }}
          >
            <CloseRoundedIcon />
          </CommonButton>
          {renderStep()}
        </WrapperContent>
      </Wrapper>
      {currentStep !== STEPS.VERIFY_VOICE && (
        <FooterActions
          titleButtonContinue={
            currentStep === STEPS.INSTRUCTIONS && t('createVoice')
          }
          handleOpenTipsList={handleOpenTipsList}
          onGuideClick={() =>
            window.open(VOICE_CLONING_UPLOAD_GUIDE_URL, '_blank')
          }
          onContinue={handleContinue}
          loading={isLoading}
          autoSaveStatus={autoSaveStatus}
          continueDisabled={isContinueDisabled}
          continueTooltip={continueTooltip}
          showAutoSave={currentStep === STEPS.UPLOAD_AND_FORM}
        />
      )}
      <TipsList tips={tips} open={openTipsList} onClose={handleCloseTipsList} />
      {errorDialog?.open && (
        <NotificationDialogV3
          name={errorDialog?.name || 'error-dialog'}
          title={errorDialog?.title}
          description={errorDialog?.description}
          variant={errorDialog?.variant}
          open={errorDialog?.open}
          width={errorDialog?.width}
          fitWidth={errorDialog?.fitWidth}
          widthContent={errorDialog?.widthContent}
          maxWidth={errorDialog?.maxWidth}
          padding={errorDialog?.padding}
          paddingHeader={errorDialog?.paddingHeader}
          paddingAction={errorDialog?.paddingAction}
          actionComponent={errorDialog?.actionComponent}
          onClose={errorDialog?.onClose || (() => {})}
        />
      )}
      {isOpenDenoiseDialog && (
        <DenoiseDialog
          audioId={adjustingAudioId}
          initialEnabled={Boolean(adjustingAudio?.denoise)}
          onSave={handleSaveDenoiseSetting}
          onClose={handleCloseDenoiseDialog}
        />
      )}
    </StyledProfessionalVoiceCloneUpload>
  );
};

export default ProfessionalVoiceCloneUpload;
